import axios from 'axios'
import { createApp } from 'vue'
import FTab from '~/js/components/tab'
import { numberWithCommas } from '~/js/helpers'
import { getUrlWithQueryParams } from '~/js/helpers/url-helper.js'
import { addToCart } from '~/js/helpers/addToCart'
import { pushCartAddedEvent } from '~/js/gtm'

const ID_CUSTOMERS_ACCOUNT_ORDER = 'js-customersAccountOrder'
const TABS = [
  {
    value: 'ecommerce',
    label: 'オンラインでのご注文履歴',
  },
  {
    value: 'pos',
    label: '店頭でのご購入履歴',
  },
]
const LIMIT = 10
const QUERY_REORDER = '.product__cart[action="/cart/add"]'

document.addEventListener('DOMContentLoaded', async () => {
  const elementCustomersAccountOrder = document.getElementById(
    ID_CUSTOMERS_ACCOUNT_ORDER
  )
  if (!elementCustomersAccountOrder) return

  const { baseUrl, customerId, hashedCustomerId } =
    elementCustomersAccountOrder.dataset
  const purchaseHistoryUrl = `${baseUrl}/customers/store_purchase_histories`

  const fetchStorePurchaceHistory = async ({
    limit,
    continuationToken,
  } = {}) => {
    const { data } = await axios.get(purchaseHistoryUrl, {
      withCredentials: true,
      params: {
        id: customerId,
        h: hashedCustomerId,
        limit: limit || LIMIT,
        t: continuationToken,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const storePurchaseHistories = data.storePurchaseHistories
      .slice(0, limit || LIMIT)
      .map(
        (storePurchaseHistory) => new StorePurchaseHistory(storePurchaseHistory)
      )
    return {
      continuationToken: data.continuationToken,
      storePurchaseHistories,
    }
  }

  const search = new URLSearchParams(location.search)
  const tabParam = search.get('tab')
  const selectedTab = TABS.find((tab) => tab.value === tabParam) || TABS[0]
  const tabItems = TABS.map((tab) => {
    return {
      ...tab,
      title: tab.label,
      isSelected: tab.value === selectedTab.value,
    }
  })

  createApp({
    name: 'TemplatesCustomersAccountOrders',
    components: { FTab },
    data() {
      return {
        hasError: false,
        isProcessing: false,
        selectedTab,
        storePurchaseHistories: [],
        continuationToken: undefined,
        tabItems,
      }
    },
    computed: {
      showButtonShowMore() {
        return !this.isProcessing && !this.hasError && !!this.continuationToken
      },
    },
    async created() {
      if (this.selectedTab.value == TABS[1].value)
        await this.fetchStorePurchaceHistory()
    },
    methods: {
      async onTabSelected(index) {
        this.selectedTab = this.tabItems[index]
        this.tabItems.forEach(
          (tabItem, i) => (tabItem.isSelected = i === index)
        )

        const state = { tab: this.selectedTab.value }
        const url = getUrlWithQueryParams({ tab: this.selectedTab.value })
        history.pushState(state, '', url)
        if (
          this.selectedTab.value == TABS[1].value &&
          !this.storePurchaseHistories.length
        )
          await this.fetchStorePurchaceHistory()
      },

      async fetchStorePurchaceHistory() {
        this.isProcessing = true
        try {
          const { storePurchaseHistories, continuationToken } =
            await fetchStorePurchaceHistory({
              limit: LIMIT,
              continuationToken: this.continuationToken,
            })
          this.storePurchaseHistories = [
            ...this.storePurchaseHistories,
            ...storePurchaseHistories,
          ]
          this.continuationToken = encodeURIComponent(continuationToken || '')
          this.hasError = false
        } catch (error) {
          this.hasError = true
          console.error(error)
        }
        this.isProcessing = false
      },
      async retry() {
        this.fetchStorePurchaceHistory()
      },
    },
  }).mount(elementCustomersAccountOrder)

  const elementsButtonReorder = document.querySelectorAll(QUERY_REORDER)
  if (!elementsButtonReorder.length) return

  elementsButtonReorder.forEach((element) => {
    element.addEventListener('submit', async (event) => {
      event.preventDefault()
      const form = event.target
      const item = {
        handle: form.dataset.handle,
        id: form.dataset.variantId,
        image: form.dataset.productImage || '',
        productTitle: form.dataset.productTitle || '',
        productTitlePrefix: form.dataset.productTitlePrefix || '',
        quantity: 1,
      }
      const { itemsAdded, itemsAddFailed } = await addToCart([item])
      window.dispatchEvent(
        new CustomEvent('cart-added', {
          detail: { items: itemsAdded, itemsAddFailed },
        })
      )
      pushCartAddedEvent({ items: itemsAdded })
    })
  })
})

const getPriceText = (price = 0) => {
  return price < 0
    ? `- ¥ ${numberWithCommas(-price)}`
    : `¥ ${numberWithCommas(price)}`
}
class StorePurchaseHistory {
  constructor(storePurchaseHistory = {}) {
    this.grantedPoint = storePurchaseHistory.grantedPoint
    this.orderedDateOnBusinessDay =
      storePurchaseHistory.orderedDateOnBusinessDay
    this.storeName = storePurchaseHistory.storeName
    this.storePurchasedProducts =
      storePurchaseHistory.storePurchasedProducts.map(
        (product) => new StorePurchaseProduct(product)
      )
  }

  get grantedPointText() {
    return numberWithCommas(this.grantedPoint)
  }

  get orderTotalText() {
    const orderTotal = this.storePurchasedProducts.reduce(
      (total, product) => (total += product.purchasePriceWithTaxAndDiscount),
      0
    )
    return getPriceText(orderTotal)
  }
}

class StorePurchaseProduct {
  constructor(product = {}) {
    this.purchasePriceWithTaxAndDiscount = parseInt(
      product.purchasePriceWithTaxAndDiscount
    )
    this.productName = product.productName || ''
    this.purchaseCount = product.purchaseCount || ''
  }
  get purchasePriceWithTaxAndDiscountText() {
    return getPriceText(this.purchasePriceWithTaxAndDiscount)
  }
}
